<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import * as signalR from '@microsoft/signalr';
    import { BCollapse, BMessage } from 'buefy';
    import { longDate } from '@/formatters.ts';
    import { getRelease } from '@/shared/helpers/api';
    import type { Release, ReleaseSpecification, SubReleaseState } from '@/shared/helpers/api/types/entities.ts';
    import { getApplicationLink, getCodeSetLink, getLibraryLink } from '@/shared/helpers/routing';
    import { http } from '@/shared/httpWrapper';
    import ReleaseErrorsAndWarnings from './ReleaseErrorsAndWarnings.vue';
    import ReleaseStateChanger from './ReleaseStateChanger.vue';
    import ApplicationLink from './ApplicationLink.vue';

    const props = withDefaults(defineProps<{
        subreleases?: SubReleaseState[];
    }>(), {
        subreleases: () => []
    });

    const releases = ref<Release[]>([]);

    const sortedReleases = computed(() => releases.value.toSorted((a, b) => a.id - b.id));

    const failedSignalRConnection = ref<boolean>(false);

    const connection = new signalR.HubConnectionBuilder()
        .withUrl(window.config!.signalRUrlRelease, {
            accessTokenFactory: async () => await window.authService.acquireToken()
        })
        .configureLogging(signalR.LogLevel.None)
        .build();

    async function loadRelease(releaseId: number): Promise<void> {
        const updatedRelease = await getRelease(http, releaseId);
        const index = releases.value.findIndex(release => release.id === releaseId);

        if (index > -1) {
            releases.value[index] = updatedRelease;
        } else {
            releases.value.push(updatedRelease);
        }
    }

    function getCompositeLibraries(release: Release): string[] {
        if (release.specification.specificationType === 'CompositeChangeDocument') {
            return release.specification.libraries;
        }
        return [];
    }

    function getCompositeCodeSetDescriptions(release: Release): string[] {
        if (release.specification.specificationType === 'CompositeChangeDocument') {
            return release.specification.changeDocuments
                .map(document => document.codeSetDescription)
                .filter((description): description is string => description !== null && description.length > 0);
        }
        return [];
    }

    function isCompositeReleaseSpec(spec: ReleaseSpecification) {
        if (spec.specificationType === 'CompositeChangeDocument' || spec.specificationType === 'ChangeDocument') {
            return spec.isComposite;
        }
        return false;
    }

    function releaseLibraryName(release: Release): string {
        if (isCompositeReleaseSpec(release.specification)) {
            return getCompositeLibraries(release).join(', ');
        }
        return release.specification.libraryName ?? '';
    }

    onMounted(async () => {
        await Promise.all(props.subreleases.map(item => loadRelease(item.releaseId)));

        connection.on('change', async (releaseId: number) => {
            await loadRelease(releaseId);
        });

        connection.onclose(() => failedSignalRConnection.value = true);

        try {
            await connection.start();
            await Promise.all(releases.value.map((release) => {
                return connection.invoke('ReleaseId', `${release.id}`);
            }));
        } catch {
            failedSignalRConnection.value = true;
        }
    });

    onBeforeUnmount(() => {
        void connection.stop();
    });
</script>

<template>
    <div
        v-if="releases.length > 0">
        <h3 class="title is-3">
            Subreleases:
        </h3>
        <b-message
            v-if="failedSignalRConnection"
            type="is-warning"
            title="Live update warning">
            <p>Failed to connect to notification hub, release statuses might not be updated.</p>
            <p>Try refreshing the page</p>
        </b-message>
        <div
            v-for="rel in sortedReleases"
            :key="rel.id"
            class="box is-shadowless has-border has-background-white-ter">
            <h5 class="title is-5">
                Release id:
                <router-link :to="{ name: 'ReleaseById', params: { release: rel.id } }">
                    {{ rel.id }}
                </router-link>
                ({{ releaseLibraryName(rel) }})
            </h5>
            <release-errors-and-warnings
                :release="rel"
                class="mb-5" />
            <release-state-changer
                :release="rel"
                :log-button="true"
                @transition="loadRelease($event)"
                @changeState="rel.state = $event" />
            <b-collapse
                class="box"
                :model-value="false"
                animation="slide">
                <template #trigger="collapseProps">
                    <div>
                        <p class="is-size-5">
                            {{ collapseProps.open ? 'Hide' : 'Show more info' }}
                            <span class="icon is-small">
                                <i
                                    v-if="collapseProps.open"
                                    class="mdi mdi-chevron-up" />
                                <i
                                    v-else
                                    class="mdi mdi-chevron-down" />
                            </span>
                        </p>
                    </div>
                </template>
                <br>

                <div class="columns">
                    <div class="column is-one-third-desktop is-full-tablet is-full-mobile">
                        <div class="box">
                            <table class="table is-striped is-narrow is-fullwidth">
                                <tbody>
                                    <tr>
                                        <th>State</th>
                                        <td>{{ rel.state }}</td>
                                    </tr>
                                    <tr>
                                        <th>Type</th>
                                        <td>{{ rel.specification.specificationType }}</td>
                                    </tr>
                                    <tr>
                                        <th>Library</th>
                                        <td>
                                            <p v-if="isCompositeReleaseSpec(rel.specification)">
                                                {{ getCompositeLibraries(rel).join(', ') }}
                                            </p>
                                            <router-link
                                                v-if="rel.specification.libraryName"
                                                :to="getLibraryLink(rel.specification.libraryName)">
                                                {{ rel.specification.libraryName }}
                                            </router-link>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Library set</th>
                                        <td>
                                            <p v-if="isCompositeReleaseSpec(rel.specification)">
                                                {{ getCompositeCodeSetDescriptions(rel).join(', ') }}
                                            </p>
                                            <a
                                                v-if="rel.specification.codeSetName"
                                                :href="getCodeSetLink(rel.specification.codeSetName)">
                                                {{ rel.specification.codeSetDescription }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Application</th>
                                        <td>
                                            <router-link
                                                v-if="rel.specification.applicationName"
                                                :to="getApplicationLink(rel.specification.applicationName)">
                                                {{ rel.specification.applicationName }}
                                            </router-link>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Created</th>
                                        <td>{{ longDate(rel.dateCreated) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Updated</th>
                                        <td>{{ longDate(rel.dateUpdated ?? '') }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="column">
                        <div
                            v-if="rel.jobs.length"
                            class="box">
                            <h2 class="title is-5">
                                Jobs
                            </h2>
                            <table class="table is-narrow is-hoverable is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>Id</th>
                                        <th>Type</th>
                                        <th>Application</th>
                                        <th>&nbsp;</th>
                                        <th>State</th>
                                        <th>Retry count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="job in rel.jobs"
                                        :key="job.id">
                                        <td>{{ job.id }}</td>
                                        <td>{{ job.type }}</td>
                                        <td>
                                            <application-link :job="job" />
                                        </td>
                                        <td style="vertical-align: middle">
                                            <progress
                                                v-show="job.state === 'InProgress'"
                                                class="progress is-info"
                                                max="100" />
                                        </td>
                                        <td>{{ job.state }}</td>
                                        <td>{{ job.retryCount }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </b-collapse>
        </div>
    </div>
</template>

<style>

</style>
